import { faCheckCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Form, FormCheckbox, FormControlsObject, FormGroup, FormInput, Tab, Tabs } from "dolfo2"
import _ from "lodash"
import { ITournamentPokemonGo } from "pkm-special-tool"
import React from "react"
import { makeApiCall, ServerCall, showMessage } from "../../../apis/ServerCall"
import { Listable } from "../../../interfaces/IUtils"
import { CanLoad } from "../../../utils/CanLoad"
import { translate } from "../../../utils/LangUtils"
import { ISendTeamProps, TEAM_LIST_URL } from "../SendTeamList"

const range = _.range(1, 7),
REQUIRED_KEYS: (keyof ITournamentPokemonGo)[] = ["slot", "pokemon", "cp", "move1", "hp"],
BOOLEAN_KEYS: (keyof ITournamentPokemonGo)[] = ["buddy", "purified", "shadow"]

type IProps = Listable<ITournamentPokemonGo> & ISendTeamProps
type IState = Listable<FormGroup<Partial<ITournamentPokemonGo>>>

export class PokemonGoTeamList extends CanLoad<IProps, IState>{
    constructor(props: IProps){
        super(props, {
            list: range.map(slot => {
                if(props.list.some(d => d.slot === slot))
                    return props.list.find(d => d.slot === slot)

                return {
                    slot,
                    pokemon: "",
                    cp: null,
                    hp: null,
                    buddy: null,
                    shadow: null,
                    purified: null,
                    move1: "",
                    move2: "",
                    move3: "",
                    move4: "",
                    notes: ""
                } as Partial<ITournamentPokemonGo>
            }).map(d => {
                let obj: FormControlsObject<Partial<ITournamentPokemonGo>> = {}

                Object.entries(d).forEach(([k, v]) => obj = {
                    ...obj,
                    [k]: {
                        value: v,
                        required: REQUIRED_KEYS.includes(k as keyof ITournamentPokemonGo) && !props.isAdmin
                    }
                })

                const form = new FormGroup(obj)
                
                if(props.isAdmin)
                    form.disable()

                return form
            })
        })
    }

    private submit = () => {
        const list = this.state.list.map(d => d.getObjectValue()).filter(d => !this.isItemEmpty(d)),
        first4Slots = list.filter(l => l.slot <= 4)

        if(first4Slots.length !== 4)
            return showMessage(translate("apiErrors.go.minPokemons"))

        for(const item of list){
            const form = this.state.list.find(d => d.get("slot") === item.slot)

            if(!form.isValid() && !item.notes.trim())
                return showMessage(translate("teamList.invalidPokemon"))
        }

        this.state.list.forEach(d => d.disable())
        this.toggleLoading()

        this.props.save((id, onSuccess) => makeApiCall({
            promise: ServerCall.post(TEAM_LIST_URL + "/" + id + "/go-list", list),
            onSuccess,
            doFinally: () => {
                this.state.list.forEach(d => d.enable())
                this.toggleLoading()
            }
        }))
    }

    private isItemEmpty = (item: object) => {
        const entries = Object.entries(item).filter(([k]) => k !== "slot" && k !== "tournamentSubscription")
        return entries.every(([k, v]) => 
            v == null ||
            (_.isString(v) && v?.trim() === "") ||
            (BOOLEAN_KEYS.includes(k as keyof ITournamentPokemonGo) && v !== true)
        )
    }

    render = () => <>
        <Tabs>
            {
                _.range(1, 7).map(i => <Tab key={i} label={<>
                    Pokémon {i}
                    {!this.isItemEmpty(this.state.list[i - 1].getObjectValue()) && this.props.isAdmin && <FontAwesomeIcon icon={faCheckCircle} className="ms-1 fc-green" />}
                </>}>
                    <Form frm={this.state.list[i - 1]}>
                        <div className="row" key={i}>
                            <div className="col-12 col-md-10">
                                <div className="row">
                                    <div className="col-8">
                                        <FormInput maxLength={50} controlName="pokemon" label={translate("pokemon.species")} />
                                    </div>
                                    <div className="col-6">
                                        <FormInput maxLength={50} controlName="move1" label={translate("pokemon.move1")} />
                                    </div>
                                    <div className="col-6">
                                        <FormInput maxLength={50} controlName="move2" label={translate("pokemon.move2")} />
                                    </div>
                                    <div className="col-6">
                                        <FormInput maxLength={50} controlName="move3" label={translate("pokemon.move3")} />
                                    </div>
                                    <div className="col-6">
                                        <FormInput maxLength={50} controlName="move4" label={translate("pokemon.move4")} />
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="row">
                                    <div className="col-6 col-md-12">
                                        <FormInput type="number" min={10} decimals={0} max={1500} controlName="cp" label={translate("pokemon.cp")} />
                                    </div>
                                    <div className="col-6 col-md-12">
                                        <FormInput type="number" min={0} decimals={0} max={999} controlName="hp" label={translate("pokemon.hp")} />
                                    </div>
                                    <div className="col-6 col-md-12">
                                        <FormCheckbox controlName="buddy" label={translate("pokemon.buddy")} />
                                    </div>
                                    <div className="col-6 col-md-12">
                                        <FormCheckbox controlName="shadow" label={translate("pokemon.shadow")} />
                                    </div>
                                    <div className="col-6 col-md-12">
                                        <FormCheckbox controlName="purified" label={translate("pokemon.purified")} />
                                    </div>
                                </div>
                            </div>
                            <div className="col-12">
                                <FormInput type="textarea" rows={5} controlName="notes" label={translate("teamList.notes")} />
                            </div>
                        </div>
                    </Form>
                </Tab>)
            }
        </Tabs>

        {!this.props.isAdmin && <div className="text-end">
            <Button loading={this.state.loading} color="green" shape="pill" onClick={this.submit}>
                {translate("teamList.submit")}
            </Button>
        </div>}
    </>
}